import ajax from './ajax';

export interface Attempt {
    _id: string;
    attemptId: string;
    examId: any;
    userId?: string;
    attemptNumber: number;
    status: string;
    startTime: string;
    endTime?: string;
    timeElapsedSeconds: number;
    totalMarks?: number;
    scoredMarks?: number;
    percentage?: number;
    passed?: boolean;
    gradingStatus?: string;
    canResume?: boolean;
    /** Present when the backend resumed an existing in-progress attempt. */
    resumed?: boolean;
    createdAt: string;
    updatedAt: string;
}

/** Answers are stored separately from the attempt document. */
export interface AttemptAnswer {
    _id: string;
    attemptId: string;
    questionId: any;
    answer?: any;
    bookmarked?: boolean;
    isCorrect?: boolean | 'partial';
    marksAwarded?: number;
    maxMarks?: number;
    gradingMethod?: string;
    gradingComments?: string;
    gradedAt?: string;
    questionOrder?: number;
    timeSpentSeconds?: number;
}

export interface AttemptWithAnswers {
    attempt: Attempt;
    answers: AttemptAnswer[];
}

export interface SubmitResult {
    attempt: Attempt;
    result: {
        scoredMarks: number;
        totalMarks: number;
        percentage: number;
        passed: boolean;
        gradingStatus: string;
        questionsGraded: number;
        totalQuestions: number;
    };
}

export interface StartAttemptData {
    examId: string;
    accessCode?: string;
}

export interface SaveAnswerData {
    questionId: string;
    answer: any;
    timeSpent?: number;
    bookmarked?: boolean;
}

class AttemptService {
    async startAttempt(data: StartAttemptData): Promise<Attempt> {
        return ajax.post('/api/attempts', data);
    }

    /** Backend responds { attempt, answers }. */
    async getAttempt(id: string): Promise<AttemptWithAnswers> {
        return ajax.get(`/api/attempts/${id}`);
    }

    async saveAnswer(attemptId: string, data: SaveAnswerData): Promise<{ success: boolean }> {
        return ajax.put(`/api/attempts/${attemptId}/answers`, data);
    }

    /** Backend responds { attempt, result }. */
    async submitAttempt(attemptId: string): Promise<SubmitResult> {
        return ajax.post(`/api/attempts/${attemptId}/submit`);
    }

    /** Backend responds { attempt, answers, gradingSummary }. */
    async getAttemptResults(attemptId: string): Promise<{ attempt: Attempt; answers: AttemptAnswer[]; gradingSummary: any }> {
        return ajax.get(`/api/attempts/${attemptId}/results`);
    }

    async getUserAttempts(examId?: string): Promise<Attempt[]> {
        const query = examId ? `?examId=${examId}` : '';
        const response = await ajax.get(`/api/attempts${query}`);
        return response?.attempts || [];
    }

    /** All attempts across every student for an exam. Admin/instructor only — 403s for other roles. */
    async getExamAttempts(examId: string): Promise<Attempt[]> {
        const response = await ajax.get(`/api/attempts/exam/${examId}?limit=500`);
        return response?.attempts || [];
    }

    /** Backend responds { attempt, answers }. */
    async resumeAttempt(attemptId: string): Promise<AttemptWithAnswers> {
        return ajax.get(`/api/attempts/${attemptId}`);
    }

    async gradeAnswer(attemptId: string, answerId: string, marks: number, comments?: string): Promise<{ message: string }> {
        return ajax.post(`/api/attempts/${attemptId}/answers/${answerId}/grade`, { marks, comments });
    }

    async updateProctoring(
        attemptId: string,
        data: { fullscreenViolations?: number; focusLossCount?: number; tabSwitchCount?: number; suspiciousActivityFlags?: string[] },
    ): Promise<{ success: boolean }> {
        return ajax.put(`/api/attempts/${attemptId}/proctoring`, data);
    }

    async abandonAttempt(attemptId: string): Promise<Attempt> {
        return ajax.post(`/api/attempts/${attemptId}/abandon`);
    }
}

export default new AttemptService();
